import React, { useEffect, useState } from "react";
import { TextField, Autocomplete, InputAdornment, SxProps, Theme, Box } from "@mui/material";
import { styled, alpha, useTheme } from "@mui/material/styles";
import usePlacesAutocomplete, { getDetails } from "use-places-autocomplete";
import { getAddressComponent } from "../utils/helper";
import { AddressComponent } from "../types/common";
import MapIcon from "@mui/icons-material/Map";
import SearchIcon from "@mui/icons-material/Search";

interface SelectMapAddressProps {
  label?: string;
  onChange: (address: AddressComponent, lat?: number, lng?: number) => void;
  error?: boolean;
  helperText?: React.ReactNode;
  countryRestrictions?: string | Array<string>;
  sx?: SxProps<Theme>;
  size?: "small" | "medium";
  fullWidth?: boolean;
}

const StyledAutocomplete = styled(Autocomplete)(({ theme }) => {
  const isDark = theme.palette.mode === "dark";
  return {
    position: "relative",
    zIndex: 1,
    pointerEvents: "auto",
    "& > *": {
      pointerEvents: "auto",
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px !important",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      position: "relative",
      minHeight: "40px",
      // Background dengan glass effect
      background: isDark
        ? `linear-gradient(135deg, ${alpha("#ffffff", 0.15)} 0%, ${alpha("#ffffff", 0.08)} 50%, ${alpha("#12903C", 0.05)} 100%)`
        : `linear-gradient(135deg, ${alpha("#ffffff", 0.25)} 0%, ${alpha("#ffffff", 0.15)} 50%, ${alpha("#12903C", 0.08)} 100%)`,
      backdropFilter: "blur(40px) saturate(180%)",
      WebkitBackdropFilter: "blur(40px) saturate(180%)",
      // Border putih yang jelas
      border: `1.5px solid ${isDark ? alpha("#ffffff", 0.4) : alpha("#ffffff", 0.8)}`,
      boxShadow: isDark
        ? "0 8px 32px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.3)"
        : "0 8px 32px rgba(18, 144, 60, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.6)",
      overflow: "visible",
      padding: "14px 18px",
      pointerEvents: "auto",
      cursor: "text",
      [theme.breakpoints.down("sm")]: {
        borderRadius: "10px !important",
        minHeight: "36px",
        padding: "8px 12px",
        "@media (orientation: landscape) and (max-width: 900px)": {
          borderRadius: "8px !important",
          minHeight: "32px",
          padding: "6px 10px",
        },
      },
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: "50%",
        background: `linear-gradient(180deg, ${alpha("#ffffff", isDark ? 0.15 : 0.25)} 0%, transparent 100%)`,
        pointerEvents: "none",
        zIndex: 0,
      },
      "&::after": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `linear-gradient(135deg, transparent 0%, ${alpha("#12903C", 0.03)} 100%)`,
        pointerEvents: "none",
        zIndex: 0,
      },
      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: isDark
          ? "0 12px 48px rgba(0, 0, 0, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.4)"
          : "0 12px 48px rgba(18, 144, 60, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.7)",
        border: `1.5px solid ${isDark ? alpha("#ffffff", 0.5) : alpha("#ffffff", 0.9)}`,
        background: isDark
          ? `linear-gradient(135deg, ${alpha("#ffffff", 0.18)} 0%, ${alpha("#ffffff", 0.1)} 50%, ${alpha("#12903C", 0.06)} 100%)`
          : `linear-gradient(135deg, ${alpha("#ffffff", 0.3)} 0%, ${alpha("#ffffff", 0.18)} 50%, ${alpha("#12903C", 0.1)} 100%)`,
      },
      "&.Mui-focused": {
        transform: "translateY(-2px)",
        boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.2)}, inset 0 1px 0 rgba(255, 255, 255, 0.7)`,
        border: `1.5px solid ${alpha("#ffffff", 0.95)}`,
        background: isDark
          ? `linear-gradient(135deg, ${alpha("#ffffff", 0.2)} 0%, ${alpha("#ffffff", 0.12)} 50%, ${alpha("#12903C", 0.08)} 100%)`
          : `linear-gradient(135deg, ${alpha("#ffffff", 0.35)} 0%, ${alpha("#ffffff", 0.22)} 50%, ${alpha("#12903C", 0.12)} 100%)`,
      },
      "& .MuiInputBase-input": {
        position: "relative",
        zIndex: 2,
        pointerEvents: "auto",
      },
      "& .MuiAutocomplete-inputRoot": {
        position: "relative",
        zIndex: 2,
        pointerEvents: "auto",
      },
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderRadius: "12px",
      border: "none",
      [theme.breakpoints.down("sm")]: {
        borderRadius: "10px",
      },
    },
    "& .MuiAutocomplete-input": {
      padding: theme.spacing(1.25, 1.5),
      fontSize: "1rem",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      maxWidth: "100%",
      [theme.breakpoints.down("sm")]: {
        padding: theme.spacing(1, 1.25),
        fontSize: "0.875rem",
        "@media (orientation: landscape) and (max-width: 900px)": {
          padding: theme.spacing(0.75, 1),
          fontSize: "0.8125rem",
        },
      },
    },
    "& .MuiInputLabel-root": {
      zIndex: 10,
      position: "relative",
      pointerEvents: "none",
      fontSize: "1rem",
      [theme.breakpoints.down("sm")]: {
        fontSize: "0.875rem",
        "@media (orientation: landscape) and (max-width: 900px)": {
          fontSize: "0.8125rem",
        },
      },
    },
    // Styling untuk dropdown popper
    "& .MuiAutocomplete-popper": {
      zIndex: 1300,
      "& .MuiPaper-root": {
        minWidth: "50%",
        width: "max-content",
        maxWidth: "50%",
      },
    },
    // Styling untuk dropdown paper
    "& .MuiAutocomplete-paper": {
      marginTop: theme.spacing(1),
      borderRadius: "12px",
      minWidth: "50% !important",
      width: "50% !important",
      background: isDark
        ? `linear-gradient(135deg, ${alpha("#0d1b2a", 0.95)} 0%, ${alpha("#0d1b2a", 0.9)} 45%, ${alpha("#12903C", 0.7)} 100%)`
        : `linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.9) 50%, rgba(18, 144, 60, 0.4) 100%)`,
      backdropFilter: "blur(24px) saturate(140%)",
      WebkitBackdropFilter: "blur(24px) saturate(140%)",
      border: `1.5px solid ${isDark ? alpha("#ffffff", 0.4) : alpha("#ffffff", 0.8)}`,
      boxShadow: isDark
        ? "0 8px 32px rgba(0, 0, 0, 0.3), 0 2px 8px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.3)"
        : "0 8px 32px rgba(18, 144, 60, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.6)",
      "& .MuiAutocomplete-option": {
        padding: theme.spacing(1.5, 2),
        borderRadius: "8px",
        margin: theme.spacing(0.5, 1),
        "&:hover": {
          backgroundColor: isDark ? alpha("#ffffff", 0.1) : alpha("#12903C", 0.1),
        },
        "&[aria-selected='true']": {
          backgroundColor: isDark ? alpha("#12903C", 0.2) : alpha("#12903C", 0.15),
          "&:hover": {
            backgroundColor: isDark ? alpha("#12903C", 0.25) : alpha("#12903C", 0.2),
          },
        },
        "&.Mui-focused": {
          backgroundColor: isDark ? alpha("#ffffff", 0.1) : alpha("#12903C", 0.1),
        },
      },
      "& .MuiAutocomplete-listbox": {
        padding: theme.spacing(1, 0),
      },
    },
  };
});

const SelectMapAddress = ({
  label = "Search Address",
  onChange,
  error,
  helperText,
  sx,
  size = "medium",
  fullWidth = true,
  countryRestrictions,
}: SelectMapAddressProps): JSX.Element => {
  const [isGoogleMapsReady, setIsGoogleMapsReady] = useState(false);

  // Check if Google Maps is already loaded (by useLoadScript or script tag)
  useEffect(() => {
    const checkGoogleMaps = () => {
      if (window.google && window.google.maps && window.google.maps.places) {
        setIsGoogleMapsReady(true);
        return true;
      }
      return false;
    };

    // Check immediately
    if (checkGoogleMaps()) {
      return;
    }

    // If not ready, check periodically
    const interval = setInterval(() => {
      if (checkGoogleMaps()) {
        clearInterval(interval);
      }
    }, 100);

    // Also listen for the initMap callback if script tag is used
    if (!(window as any).initMap) {
      (window as any).initMap = () => {
        setIsGoogleMapsReady(true);
      };
    }

    return () => {
      clearInterval(interval);
    };
  }, []);

  const { ready, suggestions, setValue, init } = usePlacesAutocomplete({
    callbackName: "initMap",
    requestOptions: {
      componentRestrictions: countryRestrictions
        ? typeof countryRestrictions === "string"
          ? { country: countryRestrictions }
          : { country: countryRestrictions }
        : { country: "us" },
    },
    initOnMount: false,
    cache: 24 * 60 * 60,
    debounce: 300,
  });

  useEffect(() => {
    // Only init when Google Maps is ready
    if (isGoogleMapsReady && ready === false) {
      init();
    }
  }, [ready, init, isGoogleMapsReady]);

  useEffect(() => {
    if (
      suggestions.status === "INVALID_REQUEST" ||
      suggestions.status === "REQUEST_DENIED"
    ) {
      init();
    }
  }, [suggestions, init]);

  const handleSelect = async (
    address: google.maps.places.AutocompletePrediction
  ) => {
    try {
      const details = await getDetails({
        placeId: address.place_id,
        fields: [
          "address_components",
          "geometry",
          "types",
          "url",
          "formatted_address",
        ],
      });

      if (typeof details === "string") {
        console.error(details);
        return;
      }

      const addrComponent = getAddressComponent(details);
      const lat = details?.geometry?.location?.lat();
      const lng = details?.geometry?.location?.lng();
      onChange(addrComponent, lat, lng);
    } catch (err) {
      console.error("Error getting address details:", err);
    }
  };

  return (
    <StyledAutocomplete
      freeSolo
      disableClearable
      fullWidth={fullWidth}
      options={suggestions.data}
      disabled={!ready}
      disablePortal={false}
      componentsProps={{
        popper: {
          style: {
            zIndex: 1300,
          },
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, 8],
              },
            },
          ],
        },
      }}
      onChange={(event, value) => {
        if (typeof value === "string" || value === null) return;
        if (
          value &&
          typeof value === "object" &&
          "place_id" in value &&
          "description" in value
        ) {
          handleSelect(value as google.maps.places.AutocompletePrediction);
        }
      }}
      onInputChange={(event, value) => {
        setValue(value);
      }}
      getOptionLabel={(prediction) => {
        if (typeof prediction === "string") return prediction;
        if (prediction && typeof prediction === "object" && "description" in prediction) {
          const pred = prediction as google.maps.places.AutocompletePrediction;
          return pred.description || "?";
        }
        return "?";
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size={size}
          InputProps={{
            ...params.InputProps,
            type: "search",
            startAdornment: (
              <InputAdornment position="start">
                <MapIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          error={error}
          helperText={helperText}
          sx={{
            "& .MuiInputLabel-root": {
              fontSize: { xs: "0.875rem", sm: size === "small" ? "0.875rem" : "1rem" },
              zIndex: 10,
              position: "relative",
              pointerEvents: "none",
            },
            "& .MuiInputBase-root": {
              position: "relative",
              zIndex: 2,
              pointerEvents: "auto",
            },
            "& .MuiInputBase-input": {
              fontSize: { xs: "0.875rem", sm: "1rem" },
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "100%",
              position: "relative",
              zIndex: 3,
              pointerEvents: "auto",
            },
            "& .MuiInputAdornment-root": {
              position: "relative",
              zIndex: 2,
              pointerEvents: "auto",
            },
            ...sx,
          }}
        />
      )}
    />
  );
};
export default SelectMapAddress;
